import { defineStore } from "pinia";
import { INSPECTION_LINKS_ENDPOINT } from "@/constants/Endpoints";
import axios from "axios";
import { LOGGER } from "@/util/logger";
import { clone } from "@/util/clone";
import { MODULE_TYPES } from "@/constants/InspectionConstants";
import dayjs from "dayjs";

export const useScheduleStore = defineStore("schedule", {
  state: () => ({
    scheduleValue: {},
    originalScheduleValue: {},
  }),
  actions: {
    getScheduleObject() {
      return clone(this.scheduleValue);
    },
    getInterval(inspTypeDetail) {
      if (inspTypeDetail.is7A57RequiredValue == "1") {
        return inspTypeDetail.inspectionInterval;
      } else {
        //checkbox is not set, reset the interval
        return 0;
      }
    },
    async loadDataObject(moduleType, brkey, inspectionId, structure) {
      if (moduleType === MODULE_TYPES.INSPECTION) {
        //if moduleType is inspection, load data from pouchDB
        this.convertToInspModuleObject(structure);
      } else if (moduleType === MODULE_TYPES.MANAGEMENT && brkey && inspectionId) {
        //if moduleType is management, load data from service call
        await this.getInspLinkDataObject(brkey, inspectionId);
      }
    },
    async saveData(moduleType, value, structure, isAccepted) {
      let isSuccess = false;
      if (!isAccepted) {
        this.setInspectionDataUpdateDateTimes(value.SNBI_INSPECTION_TYPES?.filter((insp) => !insp?.deleted));
      }
      if (moduleType === MODULE_TYPES.INSPECTION) {
        this.convertToSaveInspModuleObject(value, structure);
        isSuccess = true;
      } else if (moduleType === MODULE_TYPES.MANAGEMENT) {
        isSuccess = await this.updateData(this.convertToSaveInspLinkObject(value));
      }
      return isSuccess;
    },
    async updateData(payload) {
      let isSuccess = true;
      const url = INSPECTION_LINKS_ENDPOINT.UPDATE_SCHEDULE;
      await axios
        .put(url, payload)
        .then((response) => {
          LOGGER.logEvent(LOGGER.EventAction.Update, LOGGER.EventCat.RequestResponse, "Update Schedule", response);
          this.convertToInspLinkObject(response.data.data);
        })
        .catch((e) => {
          LOGGER.logException(e);
          isSuccess = false;
        });
      return isSuccess;
    },
    async getInspLinkDataObject(brkey, inspectionId) {
      //clear idb object
      this.scheduleValue = {};
      this.originalScheduleValue = {};
      let url = INSPECTION_LINKS_ENDPOINT.GET_SCHEDULE.replace("{brkey}", brkey).replace(
        "{inspectionId}",
        inspectionId
      );

      await axios
        .get(url)
        .then((resp) => {
          this.convertToInspLinkObject(resp.data.data);
        })
        .catch((e) => {
          LOGGER.logException(e);
        });
    },

    convertToSaveInspLinkObject(scheduleValue) {
      return {
        // Identifiers
        brkey: scheduleValue.BRKEY,
        inspectionId: scheduleValue.INSPECTIONID,

        // SnbiInspEvent entries
        snbiInspectionTypes: scheduleValue.SNBI_INSPECTION_TYPES,

        // InspectionComments
        inspectionComments: scheduleValue.INSPECTION_COMMENTS,

        // SnbiInspSchedule fields
        ftPreviousDate: scheduleValue.FT_PREVIOUS_DATE,
        rouRequired: scheduleValue.ROU_REQUIRED,
        rouPreviousDate: scheduleValue.ROU_PREVIOUS_DATE,
        rouInterval: scheduleValue.ROU_INTERVAL,
        rouNextDate: scheduleValue.ROU_NEXT_DATE,
        uwRequired: scheduleValue.UW_REQUIRED,
        uwPreviousDate: scheduleValue.UW_PREVIOUS_DATE,
        uwInterval: scheduleValue.UW_INTERVAL,
        uwNextDate: scheduleValue.UW_NEXT_DATE,
        nstmRequired: scheduleValue.NSTM_REQUIRED,
        nstmPreviousDate: scheduleValue.NSTM_PREVIOUS_DATE,
        nstmInterval: scheduleValue.NSTM_INTERVAL,
        nstmNextDate: scheduleValue.NSTM_NEXT_DATE,
        dmgPreviousDate: scheduleValue.DMG_PREVIOUS_DATE,
        indRequired: scheduleValue.IND_REQUIRED,
        indPreviousDate: scheduleValue.IND_PREVIOUS_DATE,
        indInterval: scheduleValue.IND_INTERVAL,
        indNextDate: scheduleValue.IND_NEXT_DATE,
        osRequired: scheduleValue.OS_REQUIRED,
        osPreviousDate: scheduleValue.OS_PREVIOUS_DATE,
        osInterval: scheduleValue.OS_INTERVAL,
        osNextDate: scheduleValue.OS_NEXT_DATE,
        servRequired: scheduleValue.SERV_REQUIRED,
        servPreviousDate: scheduleValue.SERV_PREVIOUS_DATE,
        servInterval: scheduleValue.SERV_INTERVAL,
        servNextDate: scheduleValue.SERV_NEXT_DATE,
        scmPreviousDate: scheduleValue.SCM_PREVIOUS_DATE,
        elemRequired: scheduleValue.ELEM_REQUIRED,
        elemPreviousDate: scheduleValue.ELEM_PREVIOUS_DATE,
        elemInterval: scheduleValue.ELEM_INTERVAL,
        elemNextDate: scheduleValue.ELEM_NEXT_DATE,
        probPreviousDate: scheduleValue.PROB_PREVIOUS_DATE,
        qaPreviousDate: scheduleValue.QA_PREVIOUS_DATE,

        // Inspection fields
        inspDate: scheduleValue.INSPDATE,
        inspUserKey: scheduleValue.INSPUSRKEY,
        inspType: scheduleValue.INSPTYPE,
        nbInspectionDone: scheduleValue.NBINSPDONE,
        lastInspection: scheduleValue.LASTINSP,
        brInspectionFreq: scheduleValue.BRINSPFREQ,
        nextInspection: scheduleValue.NEXTINSP,
        fcInspectionReq: scheduleValue.FCINSPREQ,
        fcLastInspection: scheduleValue.FCLASTINSP,
        fcInspectionFreq: scheduleValue.FCINSPFREQ,
        fcLegacyNextDate: scheduleValue.FCNEXTDATE,
        fcInspectionDone: scheduleValue.FCINSPDONE,
        uwInspectionDone: scheduleValue.UWINSPDONE,
        uwInspectionReq: scheduleValue.UWINSPREQ,
        uwLastInspection: scheduleValue.UWLASTINSP,
        uwInspectionFreq: scheduleValue.UWINSPFREQ,
        uwLegacyNextDate: scheduleValue.UWNEXTDATE,
        osInspectionDone: scheduleValue.OSINSPDONE,
        osInspectionReq: scheduleValue.OSINSPREQ,
        osLastInspection: scheduleValue.OSLASTINSP,
        osInspectionFreq: scheduleValue.OSINSPFREQ,
        osLegacyNextDate: scheduleValue.OSNEXTDATE,
        elInspectionDone: scheduleValue.ELINSPDONE,
        elInspectionReq: scheduleValue.ELINSPREQ,
        elLastInsp: scheduleValue.ELLASTINSP,
        elInspectionFreq: scheduleValue.ELINSPFREQ,
        elNextDate: scheduleValue.ELNEXTDATE,
        inspectionControlId: scheduleValue.INSPECTCONTROLID,

        // userInspection fields
        overallEndDate: scheduleValue.OVERALL_END_DATE,
        inspOrgName: scheduleValue.INSP_ORG_NAME,
        inspTeamHelper: scheduleValue.INSP_TEAM_HELPER,
        uwDiverName: scheduleValue.UW_DIVER_NAME,
        consultantHiredBy: scheduleValue.CONSULTANT_HIRED_BY,
        localInspContractNum: scheduleValue.LOCAL_INSP_CONTRACT_NUM,
        utilityRepair: scheduleValue.UTILITY_REPAIR,
        extInspFreqElig: scheduleValue.EXTINSPFREQ_ELIG,
        extInspFreqConc: scheduleValue.EXTINSPFREQ_CONC,
        method1Elig: scheduleValue.METHOD1_ELIG,
        weatherCond: scheduleValue.WEATHER_COND,
        temp: scheduleValue.TEMP,
        crewHrs: scheduleValue.CREWHRS,
        snooperHrs: scheduleValue.SNOOPERHRS,
        flaggerHrs: scheduleValue.FLAGGERHRS,
        helperHrs: scheduleValue.HELPERHRS,
        fieldEngInspCost: scheduleValue.FIELD_ENG_INSP_COST,
        riggingInspCost: scheduleValue.RIGGING_INSP_COST,
        officeInspCost: scheduleValue.OFFICE_INSP_COST,
        inAccessPortionStruct: scheduleValue.INACCESS_PORTION_STRUCT,
        nextInspType: scheduleValue.NEXT_INSP_TYPE,
        nextCraneInspDate: scheduleValue.NEXT_CRANE_INSP_DATE,

        // StructureBridge fields
        bridgeGroup: scheduleValue.BRIDGEGROUP,
        reportGroup: scheduleValue.REPORTGROUP,
        nextInspId: scheduleValue.NEXTINSPID,
      };
    },
    convertToSaveInspModuleObject(scheduleValue, structure) {
      structure.snbiInspEvents = scheduleValue.SNBI_INSPECTION_TYPES?.filter((insp) => !insp?.deleted);

      structure.snbiInspSchedule.ftPreviousDate = scheduleValue.FT_PREVIOUS_DATE;
      structure.snbiInspSchedule.rouRequired = scheduleValue.ROU_REQUIRED;
      structure.snbiInspSchedule.rouPreviousDate = scheduleValue.ROU_PREVIOUS_DATE;
      structure.snbiInspSchedule.rouInterval = scheduleValue.ROU_INTERVAL;
      structure.snbiInspSchedule.rouNextDate = scheduleValue.ROU_NEXT_DATE;
      structure.snbiInspSchedule.uwRequired = scheduleValue.UW_REQUIRED;
      structure.snbiInspSchedule.uwPreviousDate = scheduleValue.UW_PREVIOUS_DATE;
      structure.snbiInspSchedule.uwInterval = scheduleValue.UW_INTERVAL;
      structure.snbiInspSchedule.uwNextDate = scheduleValue.UW_NEXT_DATE;
      structure.snbiInspSchedule.nstmRequired = scheduleValue.NSTM_REQUIRED;
      structure.snbiInspSchedule.nstmPreviousDate = scheduleValue.NSTM_PREVIOUS_DATE;
      structure.snbiInspSchedule.nstmInterval = scheduleValue.NSTM_INTERVAL;
      structure.snbiInspSchedule.nstmNextDate = scheduleValue.NSTM_NEXT_DATE;
      structure.snbiInspSchedule.nstmPreviousDate = scheduleValue.DMG_PREVIOUS_DATE;
      structure.snbiInspSchedule.indRequired = scheduleValue.IND_REQUIRED;
      structure.snbiInspSchedule.indPreviousDate = scheduleValue.IND_PREVIOUS_DATE;
      structure.snbiInspSchedule.indInterval = scheduleValue.IND_INTERVAL;
      structure.snbiInspSchedule.indNextDate = scheduleValue.IND_NEXT_DATE;
      structure.snbiInspSchedule.osRequired = scheduleValue.OS_REQUIRED;
      structure.snbiInspSchedule.osPreviousDate = scheduleValue.OS_PREVIOUS_DATE;
      structure.snbiInspSchedule.osInterval = scheduleValue.OS_INTERVAL;
      structure.snbiInspSchedule.osNextDate = scheduleValue.OS_NEXT_DATE;
      structure.snbiInspSchedule.servRequired = scheduleValue.SERV_REQUIRED;
      structure.snbiInspSchedule.servPreviousDate = scheduleValue.SERV_PREVIOUS_DATE;
      structure.snbiInspSchedule.servInterval = scheduleValue.SERV_INTERVAL;
      structure.snbiInspSchedule.servNextDate = scheduleValue.SERV_NEXT_DATE;
      structure.snbiInspSchedule.scmPreviousDate = scheduleValue.SCM_PREVIOUS_DATE;
      structure.snbiInspSchedule.elemRequired = scheduleValue.ELEM_REQUIRED;
      structure.snbiInspSchedule.elemPreviousDate = scheduleValue.ELEM_PREVIOUS_DATE;
      structure.snbiInspSchedule.elemInterval = scheduleValue.ELEM_INTERVAL;
      structure.snbiInspSchedule.elemNextDate = scheduleValue.ELEM_NEXT_DATE;
      structure.snbiInspSchedule.probPreviousDate = scheduleValue.PROB_PREVIOUS_DATE;
      structure.snbiInspSchedule.qaPreviousDate = scheduleValue.QA_PREVIOUS_DATE;
      structure.userInspection.overallEndDate = scheduleValue.OVERALL_END_DATE;
      structure.userInspection.inspOrgName = scheduleValue.INSP_ORG_NAME;
      structure.userInspection.inspTeamHelper = scheduleValue.INSP_TEAM_HELPER;
      structure.userInspection.uwDiverName = scheduleValue.UW_DIVER_NAME;
      structure.userInspection.consultantHiredBy = scheduleValue.CONSULTANT_HIRED_BY;
      structure.userInspection.localInspContractNum = scheduleValue.LOCAL_INSP_CONTRACT_NUM;
      structure.userInspection.utilityRepair = scheduleValue.UTILITY_REPAIR;
      structure.userInspection.extInspFreqElig = scheduleValue.EXTINSPFREQ_ELIG;
      structure.userInspection.extInspFreqConc = scheduleValue.EXTINSPFREQ_CONC;
      structure.userInspection.method1Elig = scheduleValue.METHOD1_ELIG;
      structure.userInspection.weatherCond = scheduleValue.WEATHER_COND;
      structure.userInspection.temp = scheduleValue.TEMP;
      structure.userInspection.crewHrs = scheduleValue.CREWHRS;
      structure.userInspection.snooperHrs = scheduleValue.SNOOPERHRS;
      structure.userInspection.flaggerHrs = scheduleValue.FLAGGERHRS;
      structure.userInspection.helperHrs = scheduleValue.HELPERHRS;
      structure.userInspection.fieldEngInspCost = scheduleValue.FIELD_ENG_INSP_COST;
      structure.userInspection.riggingInspCost = scheduleValue.RIGGING_INSP_COST;
      structure.userInspection.officeInspCost = scheduleValue.OFFICE_INSP_COST;
      structure.userInspection.inAccessPortionStruct = scheduleValue.INACCESS_PORTION_STRUCT;
      structure.userInspection.nextInspType = scheduleValue.NEXT_INSP_TYPE;
      structure.userInspection.nextCraneInspDate = scheduleValue.NEXT_CRANE_INSP_DATE;
      structure.structureBridge.bridgeGroup = scheduleValue.BRIDGEGROUP;
      structure.structureBridge.nextInspId = scheduleValue.NEXTINSPID;
      structure.structureBridge.reportGroup = scheduleValue.REPORTGROUP;
      structure.inspection.inspectionDate = scheduleValue.INSPDATE;
      structure.inspection.inspectionUserId = scheduleValue.INSPUSRKEY;
      structure.inspection.inspectionType = scheduleValue.INSPTYPE;
      structure.inspection.inspectionControlId = scheduleValue.INSPECTCONTROLID;
      structure.inspection.nbInspectionDone = scheduleValue.NBINSPDONE;
      structure.inspection.lastInspection = scheduleValue.LASTINSP;
      structure.inspection.brInspectionFreq = scheduleValue.BRINSPFREQ;
      structure.inspection.nextInspection = scheduleValue.NEXTINSP;
      structure.inspection.fcInspectionDone = scheduleValue.FCINSPDONE;
      structure.inspection.fcInspectionReq = scheduleValue.FCINSPREQ;
      structure.inspection.fcLastInspection = scheduleValue.FCLASTINSP;
      structure.inspection.fcInspectionFreq = scheduleValue.FCINSPFREQ;
      structure.inspection.fcNextDate = scheduleValue.FCNEXTDATE;
      structure.inspection.uwInspectionDone = scheduleValue.UWINSPDONE;
      structure.inspection.uwInspectionReq = scheduleValue.UWINSPREQ;
      structure.inspection.uwLastInspection = scheduleValue.UWLASTINSP;
      structure.inspection.uwInspectionFreq = scheduleValue.UWINSPFREQ;
      structure.inspection.uwNextDate = scheduleValue.UWNEXTDATE;
      structure.inspection.osInspectionDone = scheduleValue.OSINSPDONE;
      structure.inspection.osInspectionReq = scheduleValue.OSINSPREQ;
      structure.inspection.osLastInspection = scheduleValue.OSLASTINSP;
      structure.inspection.osInspectionFreq = scheduleValue.OSINSPFREQ;
      structure.inspection.osNextDate = scheduleValue.OSNEXTDATE;
      structure.inspection.elInspectionDone = scheduleValue.ELINSPDONE;
      structure.inspection.elInspectionReq = scheduleValue.ELINSPREQ;
      structure.inspection.elLastInsp = scheduleValue.ELLASTINSP;
      structure.inspection.elInspectionFreq = scheduleValue.ELINSPFREQ;
      structure.inspection.elNextDate = scheduleValue.ELNEXTDATE;
    },

    convertToInspModuleObject(structure) {
      this.scheduleValue = {
        // Identifiers
        BRKEY: structure?.structureBridge?.brkey,
        INSPECTIONID: structure?.inspection?.inspectionId,
        INSPSTAT: structure?.inspection?.inspectionStatus,

        // Array structures
        SNBI_INSPECTION_TYPES: clone(structure?.snbiInspEvents),
        inspection: clone(structure?.inspection),

        // Future Schedule Data
        FT_PREVIOUS_DATE: structure?.snbiInspSchedule?.ftPreviousDate,
        ROU_REQUIRED: structure?.snbiInspSchedule?.rouRequired,
        ROU_PREVIOUS_DATE: structure?.snbiInspSchedule?.rouPreviousDate,
        ROU_INTERVAL: structure?.snbiInspSchedule?.rouInterval,
        ROU_NEXT_DATE: structure?.snbiInspSchedule?.rouNextDate,
        UW_REQUIRED: structure?.snbiInspSchedule?.uwRequired,
        UW_PREVIOUS_DATE: structure?.snbiInspSchedule?.uwPreviousDate,
        UW_INTERVAL: structure?.snbiInspSchedule?.uwInterval,
        UW_NEXT_DATE: structure?.snbiInspSchedule?.uwNextDate,
        NSTM_REQUIRED: structure?.snbiInspSchedule?.nstmRequired,
        NSTM_PREVIOUS_DATE: structure?.snbiInspSchedule?.nstmPreviousDate,
        NSTM_INTERVAL: structure?.snbiInspSchedule?.nstmInterval,
        NSTM_NEXT_DATE: structure?.snbiInspSchedule?.nstmNextDate,
        DMG_PREVIOUS_DATE: structure?.snbiInspSchedule?.nstmPreviousDate,
        IND_REQUIRED: structure?.snbiInspSchedule?.indRequired,
        IND_PREVIOUS_DATE: structure?.snbiInspSchedule?.indPreviousDate,
        IND_INTERVAL: structure?.snbiInspSchedule?.indInterval,
        IND_NEXT_DATE: structure?.snbiInspSchedule?.indNextDate,
        OS_REQUIRED: structure?.snbiInspSchedule?.osRequired,
        OS_PREVIOUS_DATE: structure?.snbiInspSchedule?.osPreviousDate,
        OS_INTERVAL: structure?.snbiInspSchedule?.osInterval,
        OS_NEXT_DATE: structure?.snbiInspSchedule?.osNextDate,
        SERV_REQUIRED: structure?.snbiInspSchedule?.servRequired,
        SERV_PREVIOUS_DATE: structure?.snbiInspSchedule?.servPreviousDate,
        SERV_INTERVAL: structure?.snbiInspSchedule?.servInterval,
        SERV_NEXT_DATE: structure?.snbiInspSchedule?.servNextDate,
        SCM_PREVIOUS_DATE: structure?.snbiInspSchedule?.scmPreviousDate,
        ELEM_REQUIRED: structure?.snbiInspSchedule?.elemRequired,
        ELEM_PREVIOUS_DATE: structure?.snbiInspSchedule?.elemPreviousDate,
        ELEM_INTERVAL: structure?.snbiInspSchedule?.elemInterval,
        ELEM_NEXT_DATE: structure?.snbiInspSchedule?.elemNextDate,
        PROB_PREVIOUS_DATE: structure?.snbiInspSchedule?.probPreviousDate,
        QA_PREVIOUS_DATE: structure?.snbiInspSchedule?.qaPreviousDate,

        // Page fields
        INSPDATE: structure?.inspection?.inspectionDate,
        OVERALL_END_DATE: structure?.userInspection?.overallEndDate,
        INSPUSRKEY: structure?.inspection?.inspectionUserId,
        INSPTYPE: structure?.inspection?.inspectionType,
        INSPECTCONTROLID: structure?.inspection.inspectionControlId,
        INSP_ORG_NAME: structure?.userInspection.inspOrgName,
        INSP_TEAM_HELPER: structure?.userInspection.inspTeamHelper,
        UW_DIVER_NAME: structure?.userInspection.uwDiverName,
        CONSULTANT_HIRED_BY: structure?.userInspection.consultantHiredBy,
        LOCAL_INSP_CONTRACT_NUM: structure?.userInspection?.localInspContractNum,
        UTILITY_REPAIR: structure?.userInspection?.utilityRepair,
        EXTINSPFREQ_ELIG: structure?.userInspection?.extInspFreqElig,
        EXTINSPFREQ_CONC: structure?.userInspection?.extInspFreqConc,
        METHOD1_ELIG: structure?.userInspection?.method1Elig,
        WEATHER_COND: structure?.userInspection?.weatherCond,
        TEMP: structure?.userInspection?.temp,
        CREWHRS: structure?.userInspection?.crewHrs,
        SNOOPERHRS: structure?.userInspection?.snooperHrs,
        FLAGGERHRS: structure?.userInspection?.flaggerHrs,
        HELPERHRS: structure?.userInspection?.helperHrs,
        FIELD_ENG_INSP_COST: structure?.userInspection?.fieldEngInspCost,
        RIGGING_INSP_COST: structure?.userInspection?.riggingInspCost,
        OFFICE_INSP_COST: structure?.userInspection?.officeInspCost,
        INACCESS_PORTION_STRUCT: structure?.userInspection?.inAccessPortionStruct,
        NEXT_INSP_TYPE: structure?.userInspection?.nextInspType,
        NEXT_CRANE_INSP_DATE: structure?.userInspection.nextCraneInspDate,
        BRIDGEGROUP: structure?.structureBridge?.bridgeGroup,
        NEXTINSPID: structure?.structureBridge?.nextInspId,
        REPORTGROUP: structure?.structureBridge?.reportGroup,

        // Legacy schedule table
        NBINSPDONE: structure?.inspection?.nbInspectionDone,
        LASTINSP: structure?.inspection?.lastInspection,
        BRINSPFREQ: structure?.inspection?.brInspectionFreq,
        NEXTINSP: structure?.inspection?.nextInspection,
        FCINSPDONE: structure?.inspection?.fcInspectionDone,
        FCINSPREQ: structure?.inspection?.fcInspectionReq,
        FCLASTINSP: structure?.inspection?.fcLastInspection,
        FCINSPFREQ: structure?.inspection?.fcInspectionFreq,
        FCNEXTDATE: structure?.inspection?.fcNextDate,
        UWINSPDONE: structure?.inspection?.uwInspectionDone,
        UWINSPREQ: structure?.inspection?.uwInspectionReq,
        UWLASTINSP: structure?.inspection?.uwLastInspection,
        UWINSPFREQ: structure?.inspection?.uwInspectionFreq,
        UWNEXTDATE: structure?.inspection?.uwNextDate,
        OSINSPDONE: structure?.inspection?.osInspectionDone,
        OSINSPREQ: structure?.inspection?.osInspectionReq,
        OSLASTINSP: structure?.inspection?.osLastInspection,
        OSINSPFREQ: structure?.inspection?.osInspectionFreq,
        OSNEXTDATE: structure?.inspection?.osNextDate,
        ELINSPDONE: structure?.inspection?.elInspectionDone,
        ELINSPREQ: structure?.inspection?.elInspectionReq,
        ELLASTINSP: structure?.inspection?.elLastInsp,
        ELINSPFREQ: structure?.inspection?.elInspectionFreq,
        ELNEXTDATE: structure?.inspection?.elNextDate,
      };
      this.originalScheduleValue = clone(this.scheduleValue);
    },
    convertToInspLinkObject(response) {
      this.scheduleValue = {
        // Identifiers
        BRKEY: response.brkey,
        INSPECTIONID: response.inspectionId,

        // list of inspection types
        SNBI_INSPECTION_TYPES: response.snbiInspectionTypes,

        // InspectionComments
        INSPECTION_COMMENTS: response.inspectionComments ? response.inspectionComments : [],

        // snbiInspSchedule fields
        FT_PREVIOUS_DATE: response.ftPreviousDate,
        ROU_REQUIRED: response.rouRequired,
        ROU_PREVIOUS_DATE: response.rouPreviousDate,
        ROU_INTERVAL: response.rouInterval,
        ROU_NEXT_DATE: response.rouNextDate,
        UW_REQUIRED: response.uwRequired,
        UW_PREVIOUS_DATE: response.uwPreviousDate,
        UW_INTERVAL: response.uwInterval,
        UW_NEXT_DATE: response.uwNextDate,
        NSTM_REQUIRED: response.nstmRequired,
        NSTM_PREVIOUS_DATE: response.nstmPreviousDate,
        NSTM_INTERVAL: response.nstmInterval,
        NSTM_NEXT_DATE: response.nstmNextDate,
        DMG_PREVIOUS_DATE: response.dmgPreviousDate,
        IND_REQUIRED: response.indRequired,
        IND_PREVIOUS_DATE: response.indPreviousDate,
        IND_INTERVAL: response.indInterval,
        IND_NEXT_DATE: response.indNextDate,
        OS_REQUIRED: response.osRequired,
        OS_PREVIOUS_DATE: response.osPreviousDate,
        OS_INTERVAL: response.osInterval,
        OS_NEXT_DATE: response.osNextDate,
        SERV_REQUIRED: response.servRequired,
        SERV_PREVIOUS_DATE: response.servPreviousDate,
        SERV_INTERVAL: response.servInterval,
        SERV_NEXT_DATE: response.servNextDate,
        SCM_PREVIOUS_DATE: response.scmPreviousDate,
        ELEM_REQUIRED: response.elemRequired,
        ELEM_PREVIOUS_DATE: response.elemPreviousDate,
        ELEM_INTERVAL: response.elemInterval,
        ELEM_NEXT_DATE: response.elemNextDate,
        PROB_PREVIOUS_DATE: response.probPreviousDate,
        QA_PREVIOUS_DATE: response.qaPreviousDate,

        // Inspection fields
        INSPTYPE: response.inspType,
        INSPUSRKEY: response.inspUserKey,
        INSPDATE: response.inspDate,
        NBINSPDONE: response.nbInspectionDone,
        LASTINSP: response.lastInspection,
        BRINSPFREQ: response.brInspectionFreq,
        NEXTINSP: response.nextInspection,
        FCINSPREQ: response.fcInspectionReq,
        FCLASTINSP: response.fcLastInspection,
        FCINSPFREQ: response.fcInspectionFreq,
        FCNEXTDATE: response.fcLegacyNextDate,
        FCINSPDONE: response.fcInspectionDone,
        UWINSPDONE: response.uwInspectionDone,
        UWINSPREQ: response.uwInspectionReq,
        UWLASTINSP: response.uwLastInspection,
        UWINSPFREQ: response.uwInspectionFreq,
        UWNEXTDATE: response.uwLegacyNextDate,
        OSINSPDONE: response.osInspectionDone,
        OSINSPREQ: response.osInspectionReq,
        OSLASTINSP: response.osLastInspection,
        OSINSPFREQ: response.osInspectionFreq,
        OSNEXTDATE: response.osLegacyNextDate,
        ELINSPDONE: response.elInspectionDone,
        ELINSPREQ: response.elInspectionReq,
        ELLASTINSP: response.elLastInsp,
        ELINSPFREQ: response.elInspectionFreq,
        ELNEXTDATE: response.elNextDate,
        INSPECTCONTROLID: response.inspectionControlId,

        // userInspection Fields
        OVERALL_END_DATE: response.overallEndDate,
        INSP_ORG_NAME: response.inspOrgName,
        INSP_TEAM_HELPER: response.inspTeamHelper,
        UW_DIVER_NAME: response.uwDiverName,
        CONSULTANT_HIRED_BY: response.consultantHiredBy,
        LOCAL_INSP_CONTRACT_NUM: response.localInspContractNum,
        UTILITY_REPAIR: response.utilityRepair,
        EXTINSPFREQ_ELIG: response.extInspFreqElig,
        EXTINSPFREQ_CONC: response.extInspFreqConc,
        METHOD1_ELIG: response.method1Elig,
        WEATHER_COND: response.weatherCond,
        TEMP: response.temp,
        CREWHRS: response.crewHrs,
        SNOOPERHRS: response.snooperHrs,
        FLAGGERHRS: response.flaggerHrs,
        HELPERHRS: response.helperHrs,
        FIELD_ENG_INSP_COST: response.fieldEngInspCost,
        RIGGING_INSP_COST: response.riggingInspCost,
        OFFICE_INSP_COST: response.officeInspCost,
        INACCESS_PORTION_STRUCT: response.inAccessPortionStruct,
        NEXT_INSP_TYPE: response.nextInspType,
        NEXT_CRANE_INSP_DATE: response.nextCraneInspDate,

        // StructureBridge fields
        BRIDGEGROUP: response.bridgeGroup,
        NEXTINSPID: response.nextInspId,
        REPORTGROUP: response.reportGroup,

        // Extended Interval Audit reasons
        extInspFreqEligReason: response.extInspFreqEligReason,
        method1EligReason: response.method1EligReason,
      };
      this.originalScheduleValue = clone(this.scheduleValue);
    },
    // Compare B.IE.01, B.IE.02, B.IE.03, B.IE.04, B.IE.04a, B.IE.05, B.IE.06,
    // B.IE.07, B.IE.11, and B.IE.12 against their original values
    // If any do not match, set the InspectionDataUpdateDateTime for that InspType
    setInspectionDataUpdateDateTimes(incomingValues) {
      let fieldsToCompare = [
        "inspType",
        "startDate",
        "endDate",
        "teamLeader",
        "inspectorId",
        "inspInterval",
        "dueDate",
        "rbm",
        "scopeNote",
        "equipConcat",
      ];
      let newDate = dayjs().format("YYYY-MM-DDTHH:mm:ss.SSS");
      for (const incoming of incomingValues ?? []) {
        let original = this.originalScheduleValue?.SNBI_INSPECTION_TYPES?.find(
          (orig) => orig.inspType == incoming.inspType
        );
        if (original) {
          for (const field of fieldsToCompare ?? []) {
            incoming.inspectionDataUpdateDateTime =
              incoming[field] == original[field] ? incoming.inspectionDataUpdateDateTime : newDate;
          }
        } else {
          incoming.inspectionDataUpdateDateTime = newDate;
        }
      }
    },
  },
});
